import {
  Box,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  Paper,
  Typography,
} from '@mui/material';
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';
import React, { useMemo, useState } from 'react';
import { ContainerFormFormik } from 'components/shared/container_form_formik';
import ButtonForm from 'components/buttons/button-form';
import { useStep4 } from './useStep4';

const normalizar = (texto: string): string =>
  texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const GRUPOS_SERVICOS: Array<{ id: string; titulo: string; itens: string[] }> = [
  {
    id: 'assistencia-tecnica',
    titulo: 'Assistência Técnica, Serviços e Infraestrutura Rural',
    itens: [
      'Assistência Técnica e Extensão Rural (ATER)',
      'Apoio à elaboração de projetos de crédito rural',
      'Horas-trator / Máquina (Gratuitamente)',
      'Horas-trator / Máquina (Com custo)',
      'Fornecimento de Insumos (Calcário, fertilizantes, sementes, mudas)',
      'Irrigação',
      'Acesso à água para consumo',
      'Geoprocessamento',
      'Apoio na emissão de Nota Fiscal',
      'Declaração de Produção para Inscrição Estadual',
    ],
  },
  {
    id: 'producao-agropecuaria',
    titulo: 'Produção Agropecuária',
    itens: [
      'Orizicultura (Arroz na agricultura familiar)',
      'Cacauicultura',
      'Cafeicultura',
      'Floricultura',
      'Fruticultura',
      'Mandiocultura',
      'Olericultura',
      'Plantas Medicinais, Aromáticas e Condimentares',
    ],
  },
  {
    id: 'producao-animal',
    titulo: 'Produção Animal',
    itens: [
      'Pecuária de Leite',
      'Pecuária de Corte',
      'Avicultura',
      'Apicultura',
      'Piscicultura',
    ],
  },
  {
    id: 'producao-sustentavel',
    titulo: 'Produção Sustentável e Meio Ambiente',
    itens: [
      'Produção Agroecológica',
      'Produção Orgânica',
      'Sistemas Agroflorestais (SAFs)',
      'Extrativismo e Sociobiodiversidade',
      'Preservação Ambiental',
      'Manejo Ambiental',
      'Recuperação de Áreas Degradadas',
      'Cadastro Ambiental Rural - CAR',
      'Licenciamento Ambiental',
    ],
  },
  {
    id: 'organizacao-produtiva',
    titulo: 'Organização Produtiva e Desenvolvimento Social',
    itens: [
      'Associativismo e Cooperativismo',
      'Economia Solidária',
      'Artesanato Rural',
      'Ações para Juventude Rural',
      'Ações para Mulheres Rurais',
      'Ações para Povos Indígenas',
      'Ações para Quilombolas',
      'Ações para Outros Povos e Comunidades',
    ],
  },
  {
    id: 'comercializacao',
    titulo: 'Comercialização e Acesso a Mercados',
    itens: [
      'Apoio à realização de feiras (Livres e/ou Permanentes)',
      'Comercialização em Canais Convencionais (Mercados, Hotéis)',
      'Comercialização Eletrônica (Sites, Apps)',
      'Serviço de Inspeção Municipal - SIM',
    ],
  },
  {
    id: 'compras-institucionais',
    titulo: 'Compras Institucionais e Segurança Alimentar',
    itens: [
      'Apoio ao PNAE (Merenda Escolar)',
      'Apoio ao PAA (Aquisição de Alimentos Federal)',
      'Programa Municipal de Aquisição de Alimentos (Recursos Próprios)',
      'Compras Governamentais Municipais',
      'Segurança Alimentar e Nutricional',
    ],
  },
];

const Step4: React.FC = () => {
  const {
    formulario,
    listaServicosPrestados,
    uuidNenhum,
    handleChangeServicoPrestado,
    isValid,
    handleAvancar,
    handleVoltar,
    stepAtual,
    totalSteps,
  } = useStep4();

  // Separa "Nenhum" da lista principal; "Outros" foi removido da interface
  const servicosVisiveis = listaServicosPrestados.filter(
    (item) =>
      !item.nome_servico?.toLowerCase().includes('nenhum') &&
      item.nome_servico?.toLowerCase() !== 'outros',
  );
  const [gruposAbertos, setGruposAbertos] = useState<Set<string>>(
    new Set([GRUPOS_SERVICOS[0].id]),
  );

  const gruposComItens = useMemo(() => {
    return GRUPOS_SERVICOS.map((grupo) => {
      const itensNorm = new Set(grupo.itens.map(normalizar));
      const itens = servicosVisiveis.filter((item) =>
        itensNorm.has(normalizar(item.nome_servico || '')),
      );
      return { ...grupo, itens };
    }).filter((grupo) => grupo.itens.length > 0);
  }, [servicosVisiveis]);

  const toggleGrupo = (grupoId: string) => {
    const prox = new Set(gruposAbertos);
    if (prox.has(grupoId)) {
      prox.delete(grupoId);
    } else {
      prox.add(grupoId);
    }
    setGruposAbertos(prox);
  };

  return (
    <>
      <Typography
        variant="h5"
        sx={{
          mt: 4,
          mb: 4,
          fontSize: '24px',
          fontWeight: 400,
          textAlign: 'center'
        }}
      >
        Serviços Prestados
      </Typography>
      <ContainerFormFormik title='Quais são os serviços prestados por essa Secretária/Pasta de Agricultura?'>
        <Box
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          gap={4}
          display={'flex'}
          flexDirection={'column'}
        >
          <FormControl>
            {/* Nenhum – sempre primeiro */}
            <FormControlLabel
              control={<Checkbox />}
              label='Nenhum serviço é prestado.'
              checked={formulario?.servicos_prestados.length === 0}
              onChange={() => uuidNenhum && handleChangeServicoPrestado(uuidNenhum)}
            />

            {gruposComItens.map((grupo) => (
              <Paper key={grupo.id} variant='outlined' sx={{ mb: 2 }}>
                <Box
                  onClick={() => toggleGrupo(grupo.id)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: '#f5f5f5',
                    '&:hover': { backgroundColor: '#e8e8e8' },
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>{grupo.titulo}</Typography>
                  {gruposAbertos.has(grupo.id) ? (
                    <KeyboardArrowUpOutlined sx={{ color: '#666' }} />
                  ) : (
                    <KeyboardArrowDownOutlined sx={{ color: '#666' }} />
                  )}
                </Box>

                <Collapse in={gruposAbertos.has(grupo.id)} timeout='auto' unmountOnExit>
                  <Box sx={{ p: 1 }}>
                    {grupo.itens.map((item) => (
                      <FormControlLabel
                        key={item.uuid}
                        control={<Checkbox />}
                        label={item.nome_servico}
                        checked={formulario?.servicos_prestados?.includes(item.uuid)}
                        onChange={() => handleChangeServicoPrestado(item.uuid)}
                      />
                    ))}
                  </Box>
                </Collapse>
              </Paper>
            ))}
          </FormControl>
        </Box>
      </ContainerFormFormik>
      <ButtonForm
        disabled={!isValid}
        onBack={handleVoltar}
        onAdvanced={handleAvancar}
        stepAtual={stepAtual}
        totalSteps={totalSteps}
      />
    </>
  );
};

export default Step4;
